import { type HeadToHeadOutcomes, rankByValue } from "./ranking";
import { TIMED_RANKINGS, type TimedRanking, type TimedResultDetail } from "./results";
import type { IrmCode, PhaseType } from "./vocab";

/**
 * TIMED — the races ranked by something other than the clock (cycling's
 * keirin, sprint, scratch, road and MTB races, BMX, short-track skating), the
 * sprint match, the points race. Pure, shared by OVR and the results feed.
 *
 * The old OVR ranked keirin and every race by TIME and gave a road bunch on
 * the same time one shared place; both are wrong (UCI: a race is placed by
 * finishing order, a bunch is "same time" with distinct places). Here a race
 * of PLACE ranking takes its typed finishing places as the rank, and the time
 * is only shown.
 */

const up = (s: string | null | undefined): string => (s ?? "").trim().toUpperCase();
const isNum = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);

export interface TimedMeetRef {
	sportCode: string | null | undefined;
	disciplineCode?: string | null | undefined;
	eventCode?: string | null | undefined;
	phaseType?: PhaseType | null | undefined;
	config?: Record<string, unknown> | null | undefined;
}

/** Event codes (seed `programme.data.ts`, gender letter first) whose races are placed, per discipline. */
const PLACED = {
	/** Track: sprint, keirin, scratch, elimination (the time trials and pursuits stay on TIME). */
	TRK: ["SPR", "KEI", "SCR", "ELIM"],
	/** Road: the road race and the criterium; the time trial stays on TIME. */
	RD: ["RR", "CRIT"],
	/** MTB cross-country (lapped riders are placed); downhill stays on TIME. */
	MTB: ["XCO", "XCC", "XCE"],
} as const;

/**
 * How a race ranks: the meet's `config.ranking`, else by the sport —
 * PLACE for short-track skating (SKT/STR), BMX racing, and cycling's placed
 * races (`PLACED`); TIME otherwise. A QUALIFICATION phase is always TIME
 * (the sprint's 200 m flying start, short track's time trial).
 */
export function timedRankingFor(ref: TimedMeetRef): TimedRanking {
	const own = ref.config?.ranking;
	if (typeof own === "string" && (TIMED_RANKINGS as readonly string[]).includes(own)) return own as TimedRanking;
	if (ref.phaseType === "QUALIFICATION") return "TIME";
	const s = up(ref.sportCode);
	const d = up(ref.disciplineCode);
	const e = up(ref.eventCode).replace(/^[MWXO]/, "");
	if (s === "SKT" && d === "STR") return "PLACE";
	if (s === "CYC") {
		if (d === "BMX") return "PLACE";
		const placed = (PLACED as Readonly<Record<string, readonly string[]>>)[d];
		if (placed?.some((k) => e === k || e.startsWith(k))) return "PLACE";
	}
	return "TIME";
}

// ─── Ranks ───────────────────────────────────────────────────────────────────

export interface TimedRankRow<Id> {
	id: Id;
	value: number | null;
	points: number | null;
	/** The stored (or drafted) rank: the finishing place in a PLACE race. */
	rank: number | null;
	irm: IrmCode | null;
	detail: TimedResultDetail | Record<string, unknown> | null | undefined;
}

/**
 * The ranks of a race:
 *   TIME    `rankByValue` on the time (`lowerIsBetter`)
 *   PLACE   the typed place (the rank as stored / drafted); an IRM row none
 *   POINTS  `rankByValue` on `points`, higher first, ties to the final
 *           sprint's place (UCI 3.2.130)
 * A row with `rank_locked` keeps its typed rank in TIME / POINTS too.
 */
export function timedRanks<Id>(rows: readonly TimedRankRow<Id>[], ranking: TimedRanking, lowerIsBetter: boolean): Map<Id, number | null> {
	if (ranking === "PLACE") return new Map(rows.map((r) => [r.id, r.irm === null && isNum(r.rank) ? r.rank : null]));
	const ranks =
		ranking === "POINTS"
			? rankByValue(
					rows.map((r) => ({ id: r.id, value: r.points, irm: r.irm, tieBreak: pointsRaceTieBreak(r.detail) })),
					false,
				)
			: rankByValue(
					rows.map((r) => ({ id: r.id, value: r.value, irm: r.irm })),
					lowerIsBetter,
				);
	for (const r of rows) if ((r.detail as TimedResultDetail | null | undefined)?.rank_locked === true) ranks.set(r.id, r.irm === null ? r.rank : null);
	return ranks;
}

// ─── Points race ─────────────────────────────────────────────────────────────

/** Points for a lap gained (and lost) on the field — UCI 3.2.130 (CONFIRM). */
export const POINTS_RACE_LAP_POINTS = 20;

/** A points race total: the sprint points plus 20 per lap gained, minus 20 per lap lost. Null with neither. */
export function pointsRaceTotal(detail: Pick<TimedResultDetail, "sprints" | "laps_delta"> | Record<string, unknown> | null | undefined): number | null {
	const d = (detail ?? {}) as Pick<TimedResultDetail, "sprints" | "laps_delta">;
	const sprints = (d.sprints ?? []).filter((s) => isNum(s?.points));
	const laps = isNum(d.laps_delta) ? d.laps_delta : null;
	if (sprints.length === 0 && laps === null) return null;
	return sprints.reduce((t, s) => t + s.points, 0) + (laps ?? 0) * POINTS_RACE_LAP_POINTS;
}

/** The final sprint's place (smaller wins). Missing = worse than any. */
export function pointsRaceTieBreak(detail: TimedResultDetail | Record<string, unknown> | null | undefined): number[] {
	const sprints = [...((detail as TimedResultDetail | null | undefined)?.sprints ?? [])].sort((a, b) => a.seq - b.seq);
	const last = sprints.at(-1);
	return [last && isNum(last.place) ? last.place : Number.POSITIVE_INFINITY];
}

// ─── Sprint match ────────────────────────────────────────────────────────────

export type SprintRide = NonNullable<TimedResultDetail["rides"]>[number];

export interface SprintVerdict {
	/** Rides won by each side — what `result_display` shows ("2–0"). */
	score: [number, number];
	/** Per ride: who won it (null = not ridden / not decided). */
	rides: { seq: number; winner: 0 | 1 | null }[];
	outcomes: HeadToHeadOutcomes | null;
}

/**
 * A sprint match (UCI 3.2.050, the old OVR's Calculate): best of three rides,
 * first to two wins; the decider (ride 3) counts only at 1–1. A ride is won
 * by the side marked `won`; a ride IRM (relegated, DNF) loses it to the
 * other side. A side's own IRM loses the match.
 */
export function sprintMatchVerdict(a: readonly SprintRide[] | null | undefined, b: readonly SprintRide[] | null | undefined, irm: [IrmCode | null, IrmCode | null] = [null, null]): SprintVerdict {
	const of = (list: readonly SprintRide[] | null | undefined, seq: number) => (list ?? []).find((r) => r.seq === seq);
	const score: [number, number] = [0, 0];
	const rides: SprintVerdict["rides"] = [];
	for (const seq of [1, 2, 3]) {
		if (seq === 3 && !(score[0] === 1 && score[1] === 1)) break;
		const x = of(a, seq);
		const y = of(b, seq);
		let winner: 0 | 1 | null = null;
		if (x?.won === true && y?.won !== true) winner = 0;
		else if (y?.won === true && x?.won !== true) winner = 1;
		else if (x?.irm && !y?.irm && y) winner = 1;
		else if (y?.irm && !x?.irm && x) winner = 0;
		rides.push({ seq, winner });
		if (winner !== null) score[winner]++;
		if (score[0] === 2 || score[1] === 2) break;
	}
	let outcomes: HeadToHeadOutcomes | null = score[0] === 2 ? ["WIN", "LOSS"] : score[1] === 2 ? ["LOSS", "WIN"] : null;
	if (irm[0] !== null || irm[1] !== null) outcomes = (irm[0] === null) !== (irm[1] === null) ? (irm[0] === null ? ["WIN", "LOSS"] : ["LOSS", "WIN"]) : null;
	return { score, rides, outcomes };
}

// ─── Display ─────────────────────────────────────────────────────────────────

/** "-1 LAP", "-2 LAPS", "+1 LAP" — as the UCI result sheet prints a lapped / lapping rider. Empty for 0. */
export function lapsLabel(delta: number | null | undefined): string {
	if (!isNum(delta) || delta === 0) return "";
	const n = Math.abs(delta);
	return `${delta < 0 ? "-" : "+"}${n} ${n === 1 ? "LAP" : "LAPS"}`;
}

/**
 * "s.t." (same time) flags for a placed race's rows in finishing order: a
 * rider whose time equals the rider placed just ahead (UCI road: the bunch
 * gets one time and distinct places).
 */
export function sameTimeFlags(times: readonly (number | null | undefined)[]): boolean[] {
	return times.map((t, i) => i > 0 && isNum(t) && t === times[i - 1]);
}

/**
 * A cycling sprint match: the sprint event (`…SPR`, seed `programme.data.ts`),
 * two riders, after the qualifying — best of three rides (`sprintMatchVerdict`).
 */
export function isSprintMatch(ref: { sportCode: string | null | undefined; eventCode?: string | null | undefined; phaseType?: PhaseType | null | undefined; slots: number }): boolean {
	return ref.slots === 2 && up(ref.sportCode) === "CYC" && up(ref.eventCode).replace(/^[MWXO]/, "").startsWith("SPR") && ref.phaseType !== "QUALIFICATION";
}
